import { motion } from "framer-motion";
import { Moon, Heart } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function SleepRecovery() {
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="sleep-recovery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Visual */}
        <motion.div {...reveal} className="lg:col-span-6 relative">
          <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-widget">
            <img
              src="https://images.unsplash.com/photo-1773561212546-8ef3d3cfa801?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBydW5uaW5nJTIwc3VucmlzZXxlbnwwfHx8fDE3Nzc2OTc4MjF8MA&ixlib=rb-4.1.0&q=85"
              alt="Morning runner"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zoku-ink/40 via-transparent to-transparent" />
          </div>

          {/* Floating widget: Recovery */}
          <motion.div
            animate={{ y: [-8, 8, -8] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-6 -right-4 sm:right-8 bg-white rounded-2xl p-5 shadow-widget border border-stone-100 w-60"
          >
            <div className="flex items-center gap-2 text-xs font-semibold text-zoku-ink2">
              <Heart className="w-4 h-4 text-emerald-500" /> Recovery
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-heading text-4xl font-bold text-zoku-ink">84%</span>
              <span className="text-xs text-emerald-600 font-semibold">STRONG</span>
            </div>
            <div className="mt-3 h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full w-[84%] bg-emerald-500 rounded-full" />
            </div>
            <p className="mt-3 text-[11px] text-zoku-ink3 leading-relaxed">
              HRV up 12%. Resting HR 54 bpm. Go for it today.
            </p>
          </motion.div>

          {/* Floating widget: Sleep */}
          <motion.div
            animate={{ y: [6, -10, 6] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
            className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-2xl p-5 shadow-widget border border-stone-100 w-64"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold text-zoku-ink2">
                <Moon className="w-4 h-4 text-indigo-500" /> Last night
              </div>
              <span className="text-[10px] text-zoku-ink3">12:34 – 8:36</span>
            </div>
            <div className="mt-2 flex items-end gap-3">
              <div>
                <div className="font-heading text-3xl font-bold text-zoku-ink leading-none">
                  7h 42m
                </div>
                <div className="text-[11px] text-zoku-ink3 mt-1">Sleep need met</div>
              </div>
            </div>
            <div className="mt-3 flex gap-1 h-7">
              {["#FDE68A", "#A5B4FC", "#6366F1", "#818CF8", "#A5B4FC", "#6366F1", "#FDE68A"].map(
                (c, i) => (
                  <div key={i} className="flex-1 rounded-md" style={{ background: c }} />
                )
              )}
            </div>
            <div className="mt-2 flex justify-between text-[9px] text-zoku-ink3 uppercase tracking-wider">
              <span>Awake</span>
              <span>REM</span>
              <span>Core</span>
              <span>Deep</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Copy */}
        <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="lg:col-span-6 lg:pl-8">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zoku-orange mb-4">
            Sleep & Recovery
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Wake up knowing <br />
            <span className="font-editorial text-zoku-orange">how to train today.</span>
          </h2>
          <p className="mt-5 text-lg text-zoku-ink2 leading-relaxed">
            One number tells you if you're recovered or wrecked. Paired with your sleep stages,
            HRV and resting heart rate, Zoku turns gut-feel into data-backed confidence.
          </p>

          <div className="mt-8 space-y-5">
            {[
              {
                t: "Recovery score, every morning",
                d: "A clear 0–100 score that factors HRV, RHR and sleep quality into one decision.",
              },
              {
                t: "Stages that actually matter",
                d: "REM, core, deep, awake — visualised cleanly so you spot patterns over weeks.",
              },
              {
                t: "Smart sleep window",
                d: "Zoku learns your chronotype and nudges you to bed when your body expects it.",
              },
            ].map((f, i) => (
              <div key={i} className="flex gap-4">
                <div className="shrink-0 w-8 h-8 rounded-full bg-zoku-orangeLight text-zoku-orange flex items-center justify-center text-sm font-bold">
                  {i + 1}
                </div>
                <div>
                  <h4 className="font-semibold text-zoku-ink">{f.t}</h4>
                  <p className="text-sm text-zoku-ink2 mt-0.5 max-w-md">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
